import React, { useState, useMemo, useEffect, useCallback, useRef, Activity } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";

import styled from "styled-components";
import toast from 'react-hot-toast';
import showMilestoneToast from "../../components/toast/showMilestoneToast";
import SvgLoadingAnimation from "../../components/loading";
import TopAppBarHome from "../../components/top-app-bar/TopAppBarHome";
import TitleBlock from "../../components/block/TitleBlock";
import TodoShotCard from "./components/TodoList/TodoShotCard";
import WaitingShotCard from "./components/WaitingList/WaitingShotCard";
import InProgressWheel, {
  type TodoListItem,
} from "./components/InProgressWheel/InProgressWheel";
import CompletedShotsList from "./components/CompletedList/CompletedShotsList";
import CompletedShotCard from "./components/SearchResults/CompletedShotCard";
import ShotTable from "./components/ShotTable/ShotTable";
import { ProjectMenu } from "../../components/project/ProjectMenu";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectQuery, useUpdateProjectMutation } from "../../app/services/projects";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetShotsQuery, Shot, hasValidDeliveryDate } from "../../app/services/shots";
import { useGetTodoNoteCardsQuery } from "../../app/services/notes";
import dayjs from "dayjs";
import { shotHasMedia } from "../../constant/shotTableStatus";
import { isPrimaryPosition } from "../../utils/memberPositionUtils";
import { useShortcutAction } from "../../utils/keyboardShortcuts";
import Modal from "../../components/modal/Modal";
import DateInput from "../../components/calendar/DateInput";

// 型定義のインポート
import {
  ShotStatus,
  ProjectProgress,
  ViewMode,
  MilestoneInfo
} from "../../types/home";

const WaitingListWrapper = styled.div.attrs({
  className: "u-row u-gap8"
})`
  margin-bottom: 96px;
  max-width: 100%;
  overflow-x: auto;
  flex-wrap: nowrap;
  -webkit-overflow-scrolling: touch;
  & > * {
    flex-shrink: 0;
  }
`;

// 空状態ページのスタイル
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 24px;
  padding: 48px 16px;
`;

/**
 * 完了タブ表示中は、トークルーム / DM と同じく OutletContainer の高さに収めて
 * HomeBody の内側でスクロールさせる。ウィンドウスクロールのままだと、
 * GlobalHeader の出し入れでページの高さが変わるたびにウィンドウのスクロールバーが
 * 出入りし、プロジェクトバナーごと画面が左右にずれる。
 * 他のモードはウィンドウスクロールのまま（TopAppBarHome / ShotTable の sticky が
 * ウィンドウ基準のため）。
 */
const HomeRoot = styled.div.attrs({ className: "u-col-center" })<{ $fill: boolean }>`
  ${({ $fill }) => ($fill ? "flex: 1; min-height: 0;" : "")}
`;

const HomeBody = styled.div<{ $scroll: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  ${({ $scroll }) => ($scroll ? "flex: 1; min-height: 0;" : "")}
`;

const DATE_INPUT_OPTIONS = { static: false } as const;

/** ローカル日付を "YYYY-MM-DDT00:00:00Z" 形式に変換（TZ ずれ回避） */
const toLocalDateUTC = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00Z`;
};

const COMPLETED_STATUS_FILTER = "5,6,7";
const COMPLETED_STATUSES: ShotStatus[] = [
  ShotStatus.APPROVED,
  ShotStatus.APPROVED_WITH_DELIVERY_FILE,
  ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED,
];

/**
 * クライアント側にボールがあるステータス（テイク確認待ち）。
 * クライアント / レビュアーには「やること」、制作側には「レビュー待ち」になる。
 */
const CLIENT_ACTION_STATUSES: ShotStatus[] = [
  ShotStatus.NEW,
  ShotStatus.UPDATE,
  ShotStatus.CLIENT_PENDING,
];

/** 提出予定日優先ソート: 有効な日付あり（近い順）→ なし（updated_at 昇順 = 古い順） */
const sortByDeliveryDate = (shots: Shot[]): Shot[] => {
  return [...shots].sort((a, b) => {
    const aValid = hasValidDeliveryDate(a);
    const bValid = hasValidDeliveryDate(b);
    if (aValid && bValid) {
      return dayjs(a.next_delivery_date!).diff(dayjs(b.next_delivery_date!));
    }
    if (aValid) return -1;
    if (bValid) return 1;
    return dayjs(a.updated_at).diff(dayjs(b.updated_at));
  });
};

interface HomeProps {
  /**
   * ホームタブが実際に表示中か（ProjectWrapper が他タブ・Preview 表示中は
   * false を渡す）。InProgressWheel の下部パネルは document.body へ Portal
   * しており Activity の display:none では消えないため、これで描画を制御する。
   */
  isActiveTab?: boolean;
}

const Home: React.FC<HomeProps> = ({ isActiveTab = true }) => {
  // URLパラメータからプロジェクトIDを取得
  const { projectId } = useParams<{ projectId: string }>();

  // 現在のユーザー情報を取得
  const {
    data: currentUserData,
    isLoading: isLoadingUser
  } = useGetCurrentUserQuery();

  // 現在のユーザーのプロジェクトメンバーシップ情報を取得
  const {
    data: membershipData,
    isLoading: isLoadingMembership
  } = useGetProjectMembershipQuery(projectId || "", {
    skip: !projectId ||
      projectId === "" ||
      isLoadingUser ||
      !currentUserData
  });

  // プロジェクト詳細情報を取得
  const {
    data: projectData,
    isLoading: isLoadingProject,
    refetch: refetchProject,
  } = useGetProjectQuery(
    { project_id: projectId || "" },
    {
      skip: !projectId ||
        projectId === "" ||
        isLoadingUser ||
        !currentUserData
    }
  );

  const shotsQuerySkip = !projectId ||
    projectId === "" ||
    isLoadingUser ||
    isLoadingMembership ||
    !currentUserData ||
    !membershipData;

  // 進行中ショット (status 1-4) を取得。準備中（テイク未納品 = メディア未登録）
  // も待機中グループへ合流させるため include_empty で取得する
  const {
    data: activeShotsData,
    isLoading: isLoadingActiveShots,
    refetch: refetchActiveShots,
  } = useGetShotsQuery(
    {
      project_id: projectId || "",
      status: "1,2,3,4",
      include_empty: true,
    },
    { skip: shotsQuerySkip }
  );

  // 完了ショット存在チェック (軽量: limit=1)
  const {
    data: completedCheckData,
    isLoading: isLoadingCompletedCheck,
  } = useGetShotsQuery(
    {
      project_id: projectId || "",
      status: COMPLETED_STATUS_FILTER,
      limit: 1,
    },
    { skip: shotsQuerySkip }
  );

  const isLoadingShots = isLoadingActiveShots || isLoadingCompletedCheck;
  const refetchShots = useCallback(() => {
    refetchActiveShots();
    refetchProject();
  }, [refetchActiveShots, refetchProject]);

  // プロジェクト更新用 mutation
  const [updateProject] = useUpdateProjectMutation();

  // ユーザーのポジションを取得
  const project_user_position = useMemo((): string => {
    if (membershipData?.data?.position) {
      return membershipData.data.position.toUpperCase();
    }
    return 'CLIENT';
  }, [membershipData]);

  // APIデータをショット形式にマッピング（進行中ショットのみ）
  const apiShots = useMemo((): Shot[] | null => {
    if (!activeShotsData?.data?.shots) return null;
    return activeShotsData.data.shots;
  }, [activeShotsData]);

  // Todo ショット（ユーザーがアクションすべきショット）。
  // include_empty で取得した準備中（テイク未納品）はやることに混ぜない
  const todoShots = useMemo((): Shot[] => {
    if (!apiShots) return [];

    const filtered = apiShots.filter((shot: Shot) => {
      if (!shotHasMedia(shot)) return false;
      if (project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') {
        return CLIENT_ACTION_STATUSES.includes(shot.status);
      }
      else if (['PRODUCER', 'DIRECTOR', 'ADMIN'].includes(project_user_position)) {
        return shot.status === ShotStatus.PRODUCER_PENDING;
      }
      return false;
    });

    return sortByDeliveryDate(filtered);
  }, [project_user_position, apiShots]);

  // Waiting ショット（相手側の対応待ちショット）。
  // クライアント側は制作中 (status 3) に加え、納品予定日が設定された準備中
  // （テイク未納品・status 1/2/4）も「鋭意制作中」へ合流させる。
  // 制作側の「レビュー待ち」には準備中を含めない（テイク納品済みのみ）
  const waitingItems = useMemo((): Shot[] => {
    if (!apiShots) return [];

    const filtered = apiShots.filter((shot: Shot) => {
      if (project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') {
        if (shot.status === ShotStatus.PRODUCER_PENDING) return true;
        return (
          !shotHasMedia(shot) &&
          CLIENT_ACTION_STATUSES.includes(shot.status) &&
          !!shot.next_delivery_date
        );
      }
      else if (['PRODUCER', 'DIRECTOR', 'ADMIN'].includes(project_user_position)) {
        return shotHasMedia(shot) && CLIENT_ACTION_STATUSES.includes(shot.status);
      }
      return false;
    });

    return sortByDeliveryDate(filtered);
  }, [project_user_position, apiShots]);

  // プロジェクトの進捗状況を計算
  const projectProgress = useMemo((): ProjectProgress => {
    if (projectData?.data?.project) {
      const project = projectData.data.project;
      const totalShots = project.total_shots_count ?? 0;
      const submittedShots = project.submitted_shots_count ?? 0;
      const approvedShots = project.approved_shots_count ?? 0;
      const holdShots = project.pending_shots_count ?? 0;
      const denominator = totalShots - holdShots;

      const approvalRatio =
        totalShots === 0
          ? 1
          : denominator <= 0
            ? 0
            : approvedShots / denominator;
      const currentRatio = totalShots > 0 ? submittedShots / totalShots : 0;

      return {
        approvalRatio,
        currentRatio,
        approvalPercent: Math.round(approvalRatio * 1000) / 10,
        currentPercent: Math.round(currentRatio * 1000) / 10,
        isComplete: submittedShots === totalShots
      };
    }

    return {
      approvalRatio: 0,
      currentRatio: 0,
      approvalPercent: 0,
      currentPercent: 0,
      isComplete: false
    };
  }, [projectData]);

  // 進行中ショットの有無を判定（やること / 待機中に実際に表示されるものが
  // あるか。include_empty で取得した準備中はどちらにも入らないことがあるため、
  // クエリ結果の件数ではなく表示対象で判定する）
  const hasInProgressShots = useMemo((): boolean => {
    return todoShots.length > 0 || waitingItems.length > 0;
  }, [todoShots, waitingItems]);

  // 完了ショットの有無を判定（status 5,6,7 の limit=1 クエリで判定）
  const hasCompletedShots = useMemo((): boolean => {
    const shots = completedCheckData?.data?.shots;
    return !!shots && shots.length > 0;
  }, [completedCheckData]);

  // ショットが1件でも存在するかどうか
  const hasAnyShots = useMemo((): boolean => {
    return hasInProgressShots || hasCompletedShots;
  }, [hasInProgressShots, hasCompletedShots]);

  // デフォルトのビューモードを動的に決定
  const defaultViewMode = useMemo((): ViewMode => {
    if (hasInProgressShots) return 'in_progress';
    if (hasCompletedShots) return 'completed';
    return 'in_progress';
  }, [hasInProgressShots, hasCompletedShots]);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // 延長モーダル用ステート
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [newEndDate, setNewEndDate] = useState<Date>(() => new Date(todayStart));

  // マイルストーン表示情報を計算
  const milestoneInfo = useMemo((): MilestoneInfo | null => {
    if (!projectData?.data?.project) return null;
    const project = projectData.data.project;

    const futureMilestones = (project.milestones || [])
      .filter(m => {
        const d = new Date(m.date);
        d.setHours(0, 0, 0, 0);
        return d > todayStart;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (futureMilestones.length > 0) {
      const nearest = futureMilestones[0];
      const msDate = new Date(nearest.date);
      msDate.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((msDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
      return { type: 'milestone', event: nearest.event, days: diffDays };
    }

    if (project.end_date) {
      const endDate = new Date(project.end_date);
      endDate.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((endDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays >= 0) {
        return { type: 'end_date', days: diffDays };
      }
      return { type: 'expired' };
    }

    return null;
  }, [projectData, todayStart]);

  const handleExtendEndDate = useCallback(async () => {
    if (!newEndDate || !projectId) return;
    try {
      await updateProject({
        project_id: projectId,
        end_date: toLocalDateUTC(newEndDate),
      }).unwrap();
      setShowExtensionModal(false);
    } catch {
      toast.error("終了日の更新に失敗しました");
    }
  }, [newEndDate, projectId, updateProject]);

  // セグメントコントロール用のフィルターオプションを動的に生成
  const homeFilterOptions = useMemo(() => {
    const options: { label: string; value: ViewMode }[] = [];
    if (hasInProgressShots) {
      options.push({ label: "進行中", value: "in_progress" });
    }
    if (hasCompletedShots) {
      options.push({ label: "完了", value: "completed" });
    }
    return options;
  }, [hasInProgressShots, hasCompletedShots]);

  // セグメントコントロールでのビューモード
  const [viewMode, setViewMode] = useState<ViewMode>("in_progress");
  const [visitedModes, setVisitedModes] = useState<Set<ViewMode>>(new Set(['in_progress']));
  const prevViewModeRef = useRef<ViewMode>("in_progress");

  // 検索関連の state
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ビューモードの初期値はショット一覧の初回読み込み時にのみ決定する。
  // 以降は表示中のタブが無くなった場合の退避だけを行う（納期変更などで
  // ショット一覧が再取得されるたびにタブを切り替えると、ショットリストや
  // 検索タブを開いていたユーザーが強制的に別タブへ飛ばされてしまう）。
  const hasInitializedViewModeRef = useRef(false);
  useEffect(() => {
    if (isLoadingShots || apiShots === null) return;
    if (!hasInProgressShots && !hasCompletedShots) return;

    const applyMode = (mode: ViewMode) => {
      setViewMode(mode);
      prevViewModeRef.current = mode;
      setVisitedModes(prev => new Set([...prev, mode]));
    };

    if (!hasInitializedViewModeRef.current) {
      hasInitializedViewModeRef.current = true;
      applyMode(hasInProgressShots ? 'in_progress' : 'completed');
      return;
    }

    if (viewMode === 'in_progress' && !hasInProgressShots) {
      applyMode('completed');
    } else if (viewMode === 'completed' && !hasCompletedShots) {
      applyMode('in_progress');
    }
  }, [isLoadingShots, apiShots, hasInProgressShots, hasCompletedShots, viewMode]);

  // 検索テキストのデバウンス処理
  const handleSearchTextChange = useCallback((text: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchText(text);
    }, 300);
  }, []);

  // デバウンスタイマーのクリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 検索用の API クエリ（検索モード時のみ実行）
  const {
    data: searchResultsData,
    isLoading: isLoadingSearch,
  } = useGetShotsQuery(
    {
      project_id: projectId || "",
      filter: "all",
      search: debouncedSearchText,
    },
    {
      skip: !projectId ||
        projectId === "" ||
        viewMode !== "search" ||
        !debouncedSearchText.trim()
    }
  );

  // 共有TIPSカード（ノートページで公開されたカード。未 OK のもののみ返る）
  const { data: todoCardsData } = useGetTodoNoteCardsQuery(
    { project_id: projectId || "" },
    { skip: !projectId || isLoadingUser || !currentUserData },
  );

  // ショットとカードの混在リスト: 必須カード → ショット → 通常カード
  const todoItems = useMemo((): TodoListItem[] => {
    const cards = todoCardsData?.data?.cards ?? [];
    const requiredTips = cards
      .filter((c) => c.is_required)
      .map((card): TodoListItem => ({ kind: "tip", card }));
    const normalTips = cards
      .filter((c) => !c.is_required)
      .map((card): TodoListItem => ({ kind: "tip", card }));
    const shots = todoShots.map(
      (shot): TodoListItem => ({ kind: "shot", shot }),
    );
    return [...requiredTips, ...shots, ...normalTips];
  }, [todoCardsData, todoShots]);

  // セグメントコントロールをクリックしたときの処理
  const handleViewModeChange = useCallback((value: string) => {
    const mode = value as ViewMode;
    setViewMode(mode);
    if (mode !== 'search') {
      prevViewModeRef.current = mode;
      setVisitedModes(prev => new Set([...prev, mode]));
    }
  }, []);

  // ⌥3 再押下（既にホーム表示中）: セグメント（進行中 → 完了 → ショットリスト）を
  // 循環切替する。存在しないセグメント（ショットなし等）はスキップされる。
  useShortcutAction("home-cycle", () => {
    const order: ViewMode[] = [
      ...homeFilterOptions.map((o) => o.value),
      "schedule",
    ];
    // 検索モード中など order に無い値のときは先頭へ戻す（indexOf = -1 → 0）
    const nextIndex = (order.indexOf(viewMode) + 1) % order.length;
    handleViewModeChange(order[nextIndex]);
  });

  // 検索結果が実際にある場合のみ検索タブを表示し、それ以外は前のタブを維持
  const searchResults = searchResultsData?.data?.shots || [];
  const hasSearchResults = debouncedSearchText.trim() && !isLoadingSearch && searchResults.length > 0;
  const effectiveViewMode = (viewMode === 'search' && !hasSearchResults)
    ? prevViewModeRef.current
    : viewMode;

  // 検収確認 / 完了確認は、永続トースト（`duration: Infinity`）+ ボタンという
  // 性質上、本来 Modal が適切な UI。実検収/完了処理の実装と合わせて Modal で
  // 表示する想定（TODO: 検収・完了 API が固まり次第、ここに Modal state を追加）。
  // 旧実装は `toast.custom(t => ...)` で表示していたが、ミュート中だったため
  // ロジックごと撤去している。

  // マイルストーン表示用 ref（トースト重複防止）
  const milestoneToastShownRef = useRef(false);

  // マイルストーントースト / 延長モーダルの表示
  useEffect(() => {
    if (!milestoneInfo || milestoneToastShownRef.current) return;
    milestoneToastShownRef.current = true;

    if (milestoneInfo.type === 'expired') {
      if (isPrimaryPosition(project_user_position) && projectProgress.approvalPercent < 100) {
        setShowExtensionModal(true);
      }
      return;
    }

    const label = milestoneInfo.type === 'milestone'
      ? `${milestoneInfo.event}まであと`
      : '開発期間残り';

    showMilestoneToast({
      label,
      days: milestoneInfo.days,
    });
  }, [milestoneInfo, project_user_position, projectProgress]);

  // ローディング中の表示
  const isLoading = isLoadingUser || isLoadingProject || isLoadingShots || isLoadingMembership;
  if (isLoading) {
    return <SvgLoadingAnimation />;
  }

  // データが正しく読み込まれていない場合のフォールバック
  if (!currentUserData || !projectData || !membershipData) {
    return (
      <div className="u-flex u-flex-col u-items-center u-justify-center u-h100vh">
        <p className="u-text-error">データの読み込みに失敗しました</p>
        <p className="u-mt8 u-text-caption">ページを更新してください。</p>
      </div>
    );
  }


  // 空状態ページのレンダリング（進行中・完了ともにショットが存在しない場合）
  const renderEmptyState = () => {
    if (project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') {
      return (
        <EmptyStateWrapper>
          <span className="material-symbols-rounded" style={{ fontSize: '64px', color: 'var(--soremo-light-gray)' }}>
            hourglass_empty
          </span>
          <div className="u-col-center u-gap8">
            <p className="heading-spacing">鋭意製作中</p>
            <p className="bodytext-13 u-text-light-gray">納品をお待ちください。</p>
          </div>
        </EmptyStateWrapper>
      );
    }

    return (
      <EmptyStateWrapper>
        <span className="material-symbols-rounded" style={{ fontSize: '64px', color: 'var(--soremo-light-gray)' }}>
          upload_file
        </span>
        <div className="u-col-center u-gap8">
          <p className="heading-spacing">納品物がありません</p>
          <p className="bodytext-13 u-text-light-gray">最初の納品を行いましょう。</p>
        </div>
      </EmptyStateWrapper>
    );
  };

  // 検索結果をステータスとポジションで分類（Home画面と同じロジック）
  const categorizeSearchResults = (shots: Shot[]) => {
    const todoResults: Shot[] = [];
    const waitingResults: Shot[] = [];
    const completedResults: Shot[] = [];

    shots.forEach((shot: Shot) => {
      if (COMPLETED_STATUSES.includes(shot.status)) {
        completedResults.push(shot);
        return;
      }

      if (project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') {
        if ([ShotStatus.NEW, ShotStatus.UPDATE, ShotStatus.CLIENT_PENDING].includes(shot.status)) {
          todoResults.push(shot);
        } else if (shot.status === ShotStatus.PRODUCER_PENDING) {
          waitingResults.push(shot);
        }
      } else if (['PRODUCER', 'DIRECTOR', 'ADMIN'].includes(project_user_position)) {
        if (shot.status === ShotStatus.PRODUCER_PENDING) {
          todoResults.push(shot);
        } else if ([ShotStatus.NEW, ShotStatus.UPDATE, ShotStatus.CLIENT_PENDING].includes(shot.status)) {
          waitingResults.push(shot);
        }
      }
    });

    return {
      todoResults: sortByDeliveryDate(todoResults),
      waitingResults: sortByDeliveryDate(waitingResults),
      completedResults,
    };
  };

  // 検索結果のレンダリング（effectiveViewMode により結果がある場合のみ表示される）
  const renderSearchResults = () => {
    const { todoResults, waitingResults, completedResults } = categorizeSearchResults(searchResults);

    return (
      <>
        {/* Todo ショット（TodoShotCard と同じ表示） */}
        {todoResults.length > 0 && (
          <div className="u-col u-gap16">
            <AnimatePresence mode="popLayout">
              {todoResults.map((shot: Shot, index: number) => (
                <motion.div
                  key={shot.id || `search-todo-${index}`}
                  layout
                  variants={todoCardVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ layout: cardLayoutTransition, duration: 0.5 }}
                >
                  <TodoShotCard
                    shot={shot}
                    index={index}
                    project_user_position={project_user_position}
                    ShotStatus={ShotStatus}
                    onShotStatusChanged={refetchShots}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Waiting ショット（WaitingShotCard と同じ表示） */}
        {waitingResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <TitleBlock
              title={(project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') ? "鋭意制作中" : "レビュー待ち"}
              description={(project_user_position === 'CLIENT' || project_user_position === 'REVIEWER') ? "次のテイクにご期待ください。" : "次の提出に集中しよう"}
            />
            <LayoutGroup>
              <WaitingListWrapper>
                <AnimatePresence mode="popLayout">
                  {waitingResults.map((shot: Shot) => (
                    <motion.div
                      key={shot.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.3 } }}
                      transition={{ layout: cardLayoutTransition, duration: 0.4 }}
                    >
                      <WaitingShotCard
                        shot={shot}
                        project_user_position={project_user_position}
                        onShotStatusChanged={refetchShots}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </WaitingListWrapper>
            </LayoutGroup>
          </motion.div>
        )}

        {/* Completed ショット（大サムネイル） */}
        {completedResults.length > 0 && (
          <div className="u-row u-gap16" style={{ alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '96px' }}>
            {completedResults.map((shot: Shot, index: number) => (
              <CompletedShotCard
                key={shot.id}
                shot={shot}
                index={index}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  const cardLayoutTransition = { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] };

  const todoCardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.3, ease: "easeIn" } },
  };

  const isCompletedView = hasAnyShots && effectiveViewMode === 'completed';

  return (
    <>
      <HomeRoot $fill={isCompletedView}>
        {/* セグメント行は wrapper の外（全幅 + 自身の padding 16px）に置き、
            右端のプロジェクトメニューをカード右端（u-wrapper-full の右端）と揃える */}
        {hasAnyShots ? (
          <TopAppBarHome
            options={homeFilterOptions}
            defaultValue={defaultViewMode}
            value={viewMode}
            onChange={handleViewModeChange}
            showSearchInput={true}
            onSearchChange={handleSearchTextChange}
            searchPlaceholder="ショット名で検索..."
            leftIcon="schedule"
            rightActions={<ProjectMenu />}
            belowProjectBanner
          />
        ) : (
          /* ショットが存在しない場合もスケジュール表からショットを作成できるようにする */
          <TopAppBarHome
            options={[]}
            value={viewMode}
            onChange={handleViewModeChange}
            leftIcon="schedule"
            rightActions={<ProjectMenu />}
            belowProjectBanner
          />
        )}
        <HomeBody
          $scroll={isCompletedView}
          className={isCompletedView ? "u-overflow-y-auto u-scrollbar-gutter-stable" : undefined}
        >
          <div className="u-wrapper-full">
            {hasAnyShots ? (
              <>
                {/* 進行中タブ: ホイール表示（やることリスト + 待機中まとめカード） */}
                {hasInProgressShots && (
                  <Activity mode={effectiveViewMode === 'in_progress' ? 'visible' : 'hidden'}>
                    <InProgressWheel
                      todoItems={todoItems}
                      waitingItems={waitingItems}
                      projectUserPosition={project_user_position}
                      active={isActiveTab && effectiveViewMode === 'in_progress'}
                      onShotStatusChanged={refetchShots}
                    />
                  </Activity>
                )}
              
                {/* 完了タブ */}
                {visitedModes.has('completed') && (
                  <Activity mode={effectiveViewMode === 'completed' ? 'visible' : 'hidden'}>
                    <CompletedShotsList onShotStatusChanged={refetchShots} />
                  </Activity>
                )}

                {/* 検索タブ */}
                <Activity mode={effectiveViewMode === 'search' ? 'visible' : 'hidden'}>
                  {renderSearchResults()}
                </Activity>

                {/* スケジュール（全ショット表）タブ */}
                {visitedModes.has('schedule') && (
                  <Activity mode={effectiveViewMode === 'schedule' ? 'visible' : 'hidden'}>
                    <ShotTable />
                  </Activity>
                )}
              </>
            ) : (
              <>
                {visitedModes.has('schedule') && (
                  <Activity mode={viewMode === 'schedule' ? 'visible' : 'hidden'}>
                    <ShotTable />
                  </Activity>
                )}
                {viewMode !== 'schedule' && (
                  /* ショットが無くても共有TIPSカードはホイールで表示する */
                  todoItems.length > 0 ? (
                    <InProgressWheel
                      todoItems={todoItems}
                      waitingItems={[]}
                      projectUserPosition={project_user_position}
                      active={isActiveTab}
                      onShotStatusChanged={refetchShots}
                    />
                  ) : (
                    renderEmptyState()
                  )
                )}
              </>
            )}
          </div>
        </HomeBody>
      </HomeRoot>
      {/* 開発期間延長モーダル */}
      {showExtensionModal && (
        <Modal
          onClose={() => setShowExtensionModal(false)}
          title="開発期間を延長しますか？"
          /* 中身が DateInput 1 つで auto だと幅が細くなりすぎるため固定幅 */
          size="default"
          primaryAction={{ label: "延長", onClick: handleExtendEndDate }}
          secondaryAction={{
            label: "いいえ",
            onClick: () => setShowExtensionModal(false),
          }}
        >
          <DateInput
            label="終了日"
            option="required"
            placeholder="終了日を選択"
            value={newEndDate}
            minDate={todayStart}
            options={DATE_INPUT_OPTIONS}
            onChange={(e: { target: { value: Date } }) => setNewEndDate(e.target.value)}
          />
        </Modal>
      )}
    </>
  );
};

export default Home;
